<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import Label from '@/components/ui/Label.vue'
import Alert from '@/components/ui/Alert.vue'

const route = useRoute()
const router = useRouter()
const { t, te } = useI18n()

const accessCode = ref('')
const passwordData = ref(null)
const loading = ref(false)
const errorKey = ref(null)
const infoKey = ref('')
const promptKey = ref('')
const uiState = ref('initial')

const inputPassword = ref('')
const showPassword = ref(false)
const inputCode = ref('')
const requiredEmail = ref('')

// Auth provider info for login button
const authProvider = ref('google')

const resendTimer = ref(0)
let timerInterval = null

const translateError = (key) => {
  if (!key) return t('errors.generic')
  const a = `errors.${key}`
  const b = key
  if (te(a)) return t(a)
  if (te(b)) return t(b)
  return key
}
const errorMessage = computed(() => {
  return errorKey.value ? translateError(errorKey.value) : null
})
const infoMessage = computed(() => {
  return infoKey.value ? translateError(infoKey.value) : ''
})
const promptMessage = computed(() => {
  return promptKey.value ? translateError(promptKey.value) : ''
})

const startResendTimer = (seconds = 60) => {
  if (timerInterval) clearInterval(timerInterval)
  resendTimer.value = seconds
  timerInterval = setInterval(() => {
    resendTimer.value--
    if (resendTimer.value <= 0) {
      clearInterval(timerInterval)
    }
  }, 1000)
}

// Fetch auth config to get the provider for this host
const fetchAuthConfig = async () => {
  try {
    // Pass the current hostname to get correct provider (proxy changes host header)
    const currentHost = window.location.hostname
    const res = await fetch(`/api/auth/config?host=${encodeURIComponent(currentHost)}`)
    if (res.ok) {
      const config = await res.json()
      authProvider.value = config.provider || 'google'
    }
  } catch (e) {
    console.error('Failed to fetch auth config:', e)
  }
}

const handleLogin = () => {
  const pendingAction = { code: accessCode.value }
  sessionStorage.setItem('pendingUnlockAction', JSON.stringify(pendingAction))
  const origin = encodeURIComponent(window.location.origin)
  window.location.href = `/api/auth/login?provider=${authProvider.value}&origin=${origin}`
}

const loginButtonText = computed(() => {
  if (authProvider.value === 'microsoft') {
    return t('login.buttonMicrosoft') || 'Sign in with Microsoft'
  }
  return t('login.buttonGoogle') || 'Sign in with Google'
})

onMounted(async () => {
  accessCode.value = route.params.code || ''
  if (!accessCode.value) {
    uiState.value = 'error'
    errorKey.value = 'unlock.errorNoCode'
  } else {
    // Fetch auth config first (needed if login is required)
    await fetchAuthConfig()

    sessionStorage.removeItem('pendingUnlockAction')
    handleUnlockAttempt()
  }
})

const handleUnlockAttempt = async () => {
  // 1. Resetovanje stanja pre zahteva
  errorKey.value = null
  infoKey.value = ''
  loading.value = true

  // 2. Priprema podataka za slanje
  const payload = { access_code: accessCode.value }
  if (uiState.value === 'needs_password') payload.password = inputPassword.value
  else if (uiState.value === 'needs_code') payload.code = inputCode.value

  let responseStatus = 0
  let responseData = {}

  try {
    const fetchOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    }
    const res = await fetch(`/api/passwords/unlock`, fetchOptions)

    responseStatus = res.status
    responseData = await res.json()

    if (!res.ok) throw new Error(responseData.detail || 'errors.generic')

    // 4. Uspešan odgovor - Određivanje sledećeg koraka
    const data = responseData
    if (data.status === 'unlocked') {
      passwordData.value = { title: data.title, content: data.content }
      uiState.value = 'warning' // Prikazi upozorenje pre tajne
    } else if (data.status === 'password_required') {
      uiState.value = 'needs_password'
      promptKey.value = 'errors.ERR_PASSWORD_REQUIRED'
    } else if (data.status === 'email_verification_required') {
      uiState.value = 'email_verification_required'
      requiredEmail.value = data.email
      promptKey.value = 'errors.ERR_EMAIL_VERIFICATION_REQUIRED'
    } else if (data.status === 'code_required') {
      uiState.value = 'needs_code'
      promptKey.value = 'errors.ERR_CODE_REQUIRED'
    } else if (data.status === 'login_required') {
      // Backend vratio provider na osnovu vlasnika tajne
      authProvider.value = data.provider || 'google'
      uiState.value = 'needs_login'
    } else {
      throw new Error('errors.generic')
    }

  } catch (e) {
    const keyFromCatch = e.message || 'errors.generic'

    // A. Show login button if login is required (instead of redirecting)
    if (keyFromCatch === 'ERR_LOGIN_REQUIRED' || keyFromCatch.toLowerCase().includes('login required')) {
        uiState.value = 'needs_login'
        return
    }

    // B. Fatalne greške -> BLOKIRAJ PRISTUP (Sakrij dugme)
    if (
        responseStatus === 429 ||
        keyFromCatch === 'ERR_TOO_MANY_FAILED_ATTEMPTS' ||
        keyFromCatch === 'ERR_CODE_EXPIRED' ||
        keyFromCatch === 'ERR_LINK_EXPIRED' ||
        keyFromCatch === 'ERR_SECRET_NOT_FOUND'
    ) {
        uiState.value = 'error'
        errorKey.value = keyFromCatch
    }
    // C. Greške unosa (Pogrešna šifra/kod) -> DOZVOLI PONOVO
    else if (
        responseStatus === 401 ||
        responseStatus === 403 ||
        keyFromCatch === 'ERR_INCORRECT_PASSWORD' ||
        keyFromCatch === 'ERR_INCORRECT_CODE'
    ) {
         errorKey.value = keyFromCatch
         if (uiState.value === 'needs_password') inputPassword.value = ''
         if (uiState.value === 'needs_code') inputCode.value = ''
    }
    // D. Sve ostale neočekivane greške -> Fatalno
    else {
        uiState.value = 'error'
        errorKey.value = keyFromCatch
    }

  } finally {
    loading.value = false
  }
}

const handleSendCode = async () => {
  loading.value = true
  errorKey.value = null
  infoKey.value = ''
  let responseStatus = 0
  let responseData = {}
  try {
    const sendCodeOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_code: accessCode.value }),
      credentials: 'include',
    }
    const res = await fetch(`/api/passwords/send_code`, sendCodeOptions)
    responseStatus = res.status
    responseData = await res.json()
    if (!res.ok) throw new Error(responseData.detail || 'errors.generic')

    uiState.value = 'needs_code'
    infoKey.value = 'unlock.codeSentInfo'
    promptKey.value = 'unlock.codeSentPrompt'

    startResendTimer()

  } catch (e) {
    const keyFromCatch = e.message || 'errors.generic'
    if (responseStatus === 429) {
      errorKey.value = keyFromCatch
      uiState.value = 'needs_code'
      startResendTimer(60)
    } else {
      errorKey.value = keyFromCatch
      if (uiState.value !== 'email_verification_required') {
          uiState.value = 'error'
      }
    }
  } finally {
    loading.value = false
  }
}

const warningTitle = computed(() =>
  te('unlock.preWarningTitle') ? t('unlock.preWarningTitle') : 'Warning'
)
const warningBody = computed(() =>
  te('unlock.preWarningBody')
    ? t('unlock.preWarningBody')
    : 'You are about to view a secret. It may be sensitive and could be set to burn after reading.'
)
const confirmText = computed(() =>
  te('unlock.preWarningConfirm') ? t('unlock.preWarningConfirm') : 'Proceed'
)
const cancelText = computed(() =>
  te('unlock.preWarningCancel') ? t('unlock.preWarningCancel') : 'Cancel'
)

const confirmShowSecret = () => { uiState.value = 'unlocked' }
const cancelShowSecret = () => { router.replace('/') }
</script>

<template>
  <div class="flex flex-col md:flex-row overflow-hidden" style="height: 100vh;">
    <!-- LEFT PANEL: Lock icon and descriptive text — hidden on mobile -->
    <div class="hidden md:flex md:w-1/2 bg-slate-800 dark:bg-white flex-col items-center justify-center px-12 py-8">
      <div class="space-y-6 text-center max-w-sm">
        <!-- Lock icon -->
        <div class="flex justify-center">
          <div class="w-20 h-20 rounded-full bg-white/10 dark:bg-black/5 flex items-center justify-center">
            <svg class="w-10 h-10 text-white dark:text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>

        <div>
          <h2 class="text-2xl font-semibold text-white dark:text-slate-900 mb-2">{{ $t('unlock.title') }}</h2>
          <p class="text-sm text-white/70 dark:text-slate-600">{{ $t('unlock.wait') }}</p>
        </div>

        <!-- State-specific left panel info -->
        <div v-if="uiState === 'unlocked' && passwordData" class="bg-white/10 dark:bg-black/5 rounded-lg p-4 text-left">
          <p class="text-xs text-white/40 dark:text-slate-400 uppercase tracking-wider mb-1">{{ $t('unlock.contentLabel') }}</p>
          <p class="text-sm font-semibold text-white dark:text-slate-800">{{ passwordData.title }}</p>
        </div>

        <div v-if="uiState === 'warning' && passwordData" class="bg-yellow-500/20 dark:bg-yellow-500/10 rounded-lg p-4 text-left">
          <p class="text-xs font-semibold text-yellow-300 dark:text-yellow-600 uppercase tracking-wider mb-1">{{ warningTitle }}</p>
          <p class="text-xs text-white/70 dark:text-slate-600">{{ warningBody }}</p>
        </div>
      </div>
    </div>

    <!-- RIGHT PANEL: All unlock form content — full width on mobile -->
    <div class="flex-1 bg-white dark:bg-[#0f172a] overflow-y-auto" style="padding-top: 2.5rem;">
      <div class="min-h-full flex flex-col justify-center px-6 md:px-12 py-8">
      <div class="max-w-sm w-full mx-auto space-y-4">

        <div v-if="loading || uiState === 'initial'" class="text-center">
          <p class="text-sm text-slate-500 dark:text-slate-400">{{ $t('unlock.wait') }}</p>
          <svg class="animate-spin h-5 w-5 mx-auto mt-4 text-slate-600 dark:text-slate-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>

        <!-- Login Required State -->
        <div v-if="uiState === 'needs_login' && !loading" class="text-center space-y-4">
          <div class="py-4">
            <p class="text-slate-600 dark:text-slate-300 mb-6">
              {{ $t('unlock.loginRequired') || 'This secret requires authentication to view.' }}
            </p>
          </div>

          <!-- Microsoft Button -->
          <Button
            v-if="authProvider === 'microsoft'"
            class="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-3 text-base flex items-center justify-center"
            @click="handleLogin"
          >
            <svg class="w-5 h-5 mr-2" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="10" height="10" fill="currentColor"/>
              <rect x="11" width="10" height="10" fill="currentColor"/>
              <rect y="11" width="10" height="10" fill="currentColor"/>
              <rect x="11" y="11" width="10" height="10" fill="currentColor"/>
            </svg>
            {{ loginButtonText }}
          </Button>

          <!-- Google Button -->
          <Button
            v-else
            variant="outline"
            class="w-full py-3 text-base"
            @click="handleLogin"
          >
            <svg class="w-5 h-5 mr-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
              <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
              <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path>
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.574l6.19,5.238C39.712,35.619,44,29.831,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
            </svg>
            {{ loginButtonText }}
          </Button>
        </div>

        <div v-if="uiState === 'needs_password' && !loading">
          <Label class="text-slate-900 dark:text-white">{{ $t('unlock.passwordLabel') }}</Label>
          <div class="relative mb-4">
            <Input v-model="inputPassword" :type="showPassword ? 'text' : 'password'" @keyup.enter="handleUnlockAttempt" class="pr-10" />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 focus:outline-none"
              tabindex="-1"
            >
              <svg v-if="!showPassword" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
              </svg>
            </button>
          </div>
          <Button class="w-full rounded-md" :disabled="loading" @click="handleUnlockAttempt">
            {{ loading ? $t('unlock.checking') : $t('unlock.unlockButton') }}
          </Button>
        </div>

        <div v-if="uiState === 'email_verification_required' && !loading">
          <Alert class="mb-4"> {{ promptMessage }} </Alert>
          <Button class="w-full rounded-md" :disabled="loading" @click="handleSendCode">
            {{ loading ? $t('unlock.sendingCode') : $t('unlock.sendCodeButton') }}
          </Button>
        </div>

        <div v-if="uiState === 'needs_code' && !loading">
          <Label class="text-slate-900 dark:text-white">{{ $t('unlock.codeLabel') }}</Label>
          <Input
            v-model="inputCode"
            inputmode="numeric"
            autocomplete="one-time-code"
            @keyup.enter="handleUnlockAttempt"
            class="mb-4"
          />
          <Button class="w-full rounded-md" :disabled="loading" @click="handleUnlockAttempt">
            {{ loading ? $t('unlock.verifying') : $t('unlock.confirmCodeButton') }}
          </Button>

          <div class="mt-4 text-center">
            <p v-if="resendTimer > 0" class="text-sm text-slate-500 dark:text-slate-400">
              {{ $t('unlock.resendWait') }} ({{ resendTimer }}s)
            </p>
            <button
              v-else
              @click="handleSendCode"
              :disabled="loading"
              class="text-sm text-primary hover:text-primary/80 transition-colors"
            >
              {{ $t('unlock.resendButton') }}
            </button>
          </div>
        </div>

        <div v-if="uiState === 'warning' && passwordData" class="space-y-5">
          <div class="flex gap-3">
            <Button class="flex-1 h-10 px-4 rounded-md border border-border" @click="confirmShowSecret">
              {{ confirmText }}
            </Button>
            <button
              class="flex-1 h-10 px-4 rounded-md border border-border bg-muted hover:bg-muted/80"
              @click="cancelShowSecret"
            >
              {{ cancelText }}
            </button>
          </div>
        </div>

        <div v-if="uiState === 'unlocked' && passwordData" class="space-y-2">
          <Alert type="success"><strong>{{ passwordData.title }}</strong></Alert>
          <Label class="text-slate-900 dark:text-white">{{ $t('unlock.contentLabel') }}</Label>

          <pre
            class="p-3 rounded-md border bg-input text-foreground whitespace-pre-wrap break-words text-sm"
          >
{{ passwordData.content }}
          </pre>

          <p class="text-sm text-destructive font-medium">
            {{ $t('unlock.burnWarning') }}
          </p>
        </div>

        <Alert v-if="infoMessage && !loading" class="mt-4">{{ infoMessage }}</Alert>

        <Alert v-if="errorMessage && (uiState === 'needs_password' || uiState === 'needs_code' || uiState === 'email_verification_required') && !loading" type="error" class="mt-4">
          <strong>{{ $t('unlock.errorTitle') }}:</strong> {{ errorMessage }}
        </Alert>

        <div v-if="uiState === 'error' && !loading" class="text-center">
          <Alert type="error" class="mb-4">
            <strong>{{ $t('unlock.errorTitle') }}:</strong> {{ errorMessage || $t('errors.generic') }}
          </Alert>
          <router-link
            to="/"
            class="btn-primary h-10 px-4 rounded-md inline-flex items-center justify-center"
          >
            {{ $t('unlock.backHome') }}
          </router-link>
        </div>


        <!-- Create new secret link — always visible unless on error state -->
        <div v-if="uiState !== 'error'" class="pt-6 border-t border-slate-100 dark:border-slate-800 text-center">
          <router-link to="/" class="text-sm text-primary hover:text-primary/80 transition-colors">
            {{ $t('unlock.createNew') }}
          </router-link>
        </div>

      </div>
      </div>
    </div>
  </div>
</template>
